<template>
  <div class="transfer-template-page">
    <page-header
      :title-id="titleId"
      :title="mode === 'edit' ? 'Редактирование шаблона' : 'Новый шаблон проводки'"
      description="Настройте одну или несколько строк. Порядок строк определяет порядок формирования проводок."
    />

    <q-form ref="form" class="template-editor" data-testid="transfer-template-editor" @submit.prevent="submit">
      <form-section title="Параметры шаблона" description="Тип документа и направление применяются ко всем строкам шаблона.">
        <form-grid>
          <q-input
            v-model="model.name"
            outlined
            dense
            label="Название *"
            data-testid="template-name"
            :rules="[requiredRule('Укажите название шаблона')]"
          />
          <q-select
            v-model="model.document_type"
            outlined
            dense
            emit-value
            map-options
            label="Тип документа *"
            :options="documentTypes"
            :rules="[requiredRule('Выберите тип документа')]"
            @update:model-value="onDocumentTypeChange"
          />
          <q-select
            v-model="model.direction"
            outlined
            dense
            emit-value
            map-options
            label="Направление *"
            :options="directions"
            :rules="[requiredRule('Выберите направление')]"
          />
          <q-input v-model="model.description" outlined dense label="Описание" />
        </form-grid>
      </form-section>

      <form-section
        title="Строки проводок"
        description="Для каждой строки укажите источник суммы, счет дебета и счет кредита."
      >
        <div class="row items-center justify-between q-mb-md">
          <span class="text-caption text-grey-7">Строк: {{ model.lines.length }}</span>
          <q-btn
            outline
            no-caps
            color="primary"
            icon="add"
            label="Добавить строку"
            data-testid="template-add-line"
            @click="addLine"
          />
        </div>

        <app-state
          v-if="model.lines.length === 0"
          state="empty"
          title="Строк пока нет"
          message="Добавьте хотя бы одну строку проводки перед сохранением."
        />

        <ol v-else class="template-lines" aria-label="Строки шаблона проводок">
          <li v-for="(line, index) in model.lines" :key="line._key" class="template-line" :data-testid="`template-line-${index}`">
            <div class="template-line__header">
              <h3 class="template-line__title">Строка {{ index + 1 }}</h3>
              <div class="template-line__actions" aria-label="Изменение порядка строки">
                <q-btn
                  flat round dense icon="arrow_upward"
                  :aria-label="`Переместить строку ${index + 1} выше`"
                  :disable="index === 0"
                  @click="moveLine(index, -1)"
                />
                <q-btn
                  flat round dense icon="arrow_downward"
                  :aria-label="`Переместить строку ${index + 1} ниже`"
                  :disable="index === model.lines.length - 1"
                  @click="moveLine(index, 1)"
                />
                <q-btn
                  flat round dense icon="delete_outline" color="negative"
                  :aria-label="`Удалить строку ${index + 1}`"
                  @click="removeLine(index)"
                />
              </div>
            </div>
            <form-grid>
              <q-select
                v-model="line.amount_source"
                outlined dense emit-value map-options
                :label="`Источник суммы, строка ${index + 1} *`"
                :data-testid="`template-amount-${index}`"
                :options="amountFields"
                :rules="[requiredRule('Выберите источник суммы')]"
              />
              <q-select
                v-model="line.debit_account_id"
                outlined dense emit-value map-options
                :label="`Счет дебета, строка ${index + 1} *`"
                :data-testid="`template-debit-${index}`"
                :options="accountOptions"
                :rules="[requiredRule('Выберите счет дебета')]"
              />
              <q-select
                v-model="line.credit_account_id"
                outlined dense emit-value map-options
                :label="`Счет кредита, строка ${index + 1} *`"
                :data-testid="`template-credit-${index}`"
                :options="accountOptions"
                :rules="[requiredRule('Выберите счет кредита')]"
              />
              <q-input
                v-model="line.description_template"
                outlined dense
                :label="`Комментарий, строка ${index + 1}`"
              />
            </form-grid>
          </li>
        </ol>
      </form-section>

      <p v-if="lineError" class="template-editor__error" role="alert" tabindex="-1" ref="lineError">{{ lineError }}</p>

      <sticky-action-bar
        :state="saveState"
        :dirty="isDirty"
        :error-message="saveError"
        primary-label="Сохранить шаблон"
        primary-test-id="template-save"
        @cancel="$emit('cancel')"
        @primary="submit"
      />
    </q-form>
  </div>
</template>

<script>
import AppState from '../../../components/ui/AppState.vue'
import FormGrid from '../../../components/ui/FormGrid.vue'
import FormSection from '../../../components/ui/FormSection.vue'
import PageHeader from '../../../components/ui/PageHeader.vue'
import StickyActionBar from '../../../components/ui/StickyActionBar.vue'
import { requiredRule, validateAndFocus } from '../../../services/formValidation'
import {
  TRANSFER_DIRECTIONS,
  TRANSFER_DOCUMENT_TYPES,
  amountFieldOptions,
  createTransferTemplateLine,
  createTransferTemplateModel,
  transferTemplatePayload
} from '../../../services/transferWorkspace'

export default {
  name: 'TransferTemplateEditor',
  components: { AppState, FormGrid, FormSection, PageHeader, StickyActionBar },
  emits: ['cancel', 'save'],
  props: {
    mode: { type: String, default: 'new' },
    initialValue: { type: Object, default: () => ({ lines: [] }) },
    bills: { type: Array, default: () => [] },
    saveState: { type: String, default: 'idle' },
    saveError: { type: String, default: 'Не удалось сохранить шаблон' }
  },
  data() {
    const model = createTransferTemplateModel(this.initialValue)
    return {
      titleId: `transfer-template-${this.mode}-title`,
      model,
      initialSnapshot: JSON.stringify(transferTemplatePayload(model)),
      documentTypes: TRANSFER_DOCUMENT_TYPES,
      directions: TRANSFER_DIRECTIONS,
      requiredRule,
      lineError: ''
    }
  },
  computed: {
    amountFields() {
      return amountFieldOptions(this.model.document_type)
    },
    accountOptions() {
      return this.bills.map((bill) => ({
        value: bill.id,
        label: String(bill.full_name || bill.display_name || bill.name || bill.id)
      }))
    },
    isDirty() {
      return JSON.stringify(transferTemplatePayload(this.model)) !== this.initialSnapshot
    }
  },
  methods: {
    addLine() {
      this.lineError = ''
      this.model.lines.push(createTransferTemplateLine({}, this.model.lines.length))
      this.$nextTick(() => {
        const row = this.$el.querySelector(`[data-testid="template-line-${this.model.lines.length - 1}"]`)
        const control = row && row.querySelector('input, [tabindex="0"]')
        if (control) control.focus()
      })
    },
    removeLine(index) {
      this.model.lines.splice(index, 1)
      this.lineError = ''
    },
    moveLine(index, delta) {
      const target = index + delta
      if (target < 0 || target >= this.model.lines.length) return
      const [line] = this.model.lines.splice(index, 1)
      this.model.lines.splice(target, 0, line)
    },
    onDocumentTypeChange() {
      const allowed = new Set(this.amountFields.map((option) => option.value))
      this.model.lines.forEach((line) => {
        if (line.amount_source && !allowed.has(line.amount_source)) line.amount_source = null
      })
    },
    async submit() {
      if (this.saveState === 'saving') return
      if (this.model.lines.length === 0) {
        this.lineError = 'Добавьте хотя бы одну строку проводки.'
        await this.$nextTick()
        if (this.$refs.lineError) this.$refs.lineError.focus()
        return
      }
      this.lineError = ''
      const valid = await validateAndFocus(this.$refs.form, this)
      if (!valid) return
      this.$emit('save', transferTemplatePayload(this.model))
    }
  }
}
</script>

<style scoped>
.template-editor { min-width: 0; }
.template-lines { display: grid; gap: var(--sid-space-4); margin: 0; padding: 0; list-style: none; }
.template-line { min-width: 0; padding: var(--sid-space-4); border: 1px solid var(--sid-color-line); border-radius: var(--sid-radius-surface); background: var(--sid-color-surface-0); }
.template-line__header { display: flex; align-items: center; justify-content: space-between; gap: var(--sid-space-3); margin-bottom: var(--sid-space-3); }
.template-line__title { margin: 0; font-size: var(--sid-font-size-body); font-weight: var(--sid-font-weight-semibold); }
.template-line__actions { display: flex; align-items: center; gap: var(--sid-space-1); }
.template-editor__error { margin: var(--sid-space-4) 0 0; color: var(--sid-color-danger); }

@media (max-width: 599px) {
  .template-line { padding: var(--sid-space-3); }
  .template-line__header { align-items: flex-start; }
}
</style>
